import mixpanel, { Dict } from 'mixpanel-browser';

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

// Default config
const MIXPANEL_CONFIG = {
  debug: process.env.NODE_ENV !== 'production',
  track_pageview: false,
  persistence: 'localStorage' as const,
  record_sessions_percent: 0.0001, // effectively 0% auto-record (1-in-10000); non-zero ensures the replay module initialises so manual start_session_recording() works
  record_block_class: 'mp-block', // add class="mp-block" to hide sensitive elements from replay
};

// Platform is "web" for this app; app_version is injected at build time so the
// admin Device Analytics can break events down by release.
const APP_PLATFORM = 'web';
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || 'unknown';

export const initMixpanel = () => {
  if (MIXPANEL_TOKEN && typeof window !== 'undefined') {
    mixpanel.init(MIXPANEL_TOKEN, MIXPANEL_CONFIG);
    // Super-properties: attached to every subsequent event automatically, so the
    // admin dashboard can segment by platform / app version without extra work.
    mixpanel.register({
      platform: APP_PLATFORM,
      app_version: APP_VERSION,
    });
  }
};

/**
 * Persist the active referral code as a super-property so every event a referred
 * user fires carries `referral_code`. Registered once when a code is present.
 */
export const registerReferralCode = (referralCode: string) => {
  if (!MIXPANEL_TOKEN || typeof window === 'undefined' || !referralCode) return;
  mixpanel.register({ referral_code: referralCode });
};

export const identifyUser = (address: string, provider?: string) => {
  if (!MIXPANEL_TOKEN || typeof window === 'undefined') return;
  mixpanel.identify(address);
  if (provider) {
    mixpanel.people.set({
      WalletProvider: provider,
      ConnectedAt: new Date().toISOString(),
    });
  }
};

export const resetUser = () => {
  if (!MIXPANEL_TOKEN || typeof window === 'undefined') return;
  mixpanel.reset();
};

const SPANISH_TO_ENGLISH_MAP: Record<string, string> = {
  'política': 'politics',
  'deportes': 'sports',
  'cripto': 'crypto',
  'cultura': 'culture',
  'menciones': 'mentions',
  'clima': 'weather',
  'sí': 'yes',
  'no': 'no',
  'empate': 'draw',
  'todo': 'all',
  'todos': 'all',
  'mercado': 'market',
  'límite': 'limit',
  'comprar': 'buy',
  'vender': 'sell',
  'diario': 'daily',
  'semanal': 'weekly',
  'mensual': 'monthly',
  'activo': 'active',
  'resuelto': 'resolved'
};

const normalizeProps = (props?: Dict): Dict | undefined => {
  if (!props) return props;
  const normalized: Dict = {};
  for (const key in props) {
    if (Object.prototype.hasOwnProperty.call(props, key)) {
      const value = props[key];
      if (typeof value === 'string') {
        const lower = value.toLowerCase().trim();
        // If it perfectly matches a Spanish word in our map, swap to English.
        // Otherwise, keep the original string (preserves user inputs, IDs, titles).
        normalized[key] = SPANISH_TO_ENGLISH_MAP[lower] || value;
      } else {
        normalized[key] = value;
      }
    }
  }
  return normalized;
};

export const trackEvent = (eventName: string, props?: Dict) => {
  if (!MIXPANEL_TOKEN || typeof window === 'undefined') return;
  mixpanel.track(eventName, normalizeProps(props));
};

// ==========================================
// Session Replay
// ==========================================
export const startSessionRecording = () => {
  if (!MIXPANEL_TOKEN || typeof window === 'undefined') return;
  mixpanel.start_session_recording();
};

export const stopSessionRecording = () => {
  if (!MIXPANEL_TOKEN || typeof window === 'undefined') return;
  mixpanel.stop_session_recording();
};

// ==========================================
// Acquisition & Login
// ==========================================
export const trackLandingPageViewed = (props: {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  device_type?: string;
  referrer_url?: string;
}) => trackEvent('landing_page_viewed', props);
export const trackLoginStarted = (props: { signup_method: string; source_page: string }) =>
  trackEvent('login_started', props);
export const trackLoginSucceeded = (props: {
  login_method: string;
  days_since_last_login?: number;
  is_new_user?: boolean;
}) => trackEvent('login_succeeded', props);

// Fired once, only the first time a user authenticates (Privy `isNewUser`).
// Lets the admin dashboard separate signups from logins and measure signup
// conversion / new-vs-returning without guessing.
export const trackSignupCompleted = (props: {
  signup_method: string;
  referral_code?: string;
}) => trackEvent('signup_completed', props);

// Referral funnel. `referral_screen_viewed` fires when the auth prompt opens with
// a referral code present; `referral_applied` fires when a referred user signs up.
export const trackReferralScreenViewed = (props: { referral_code?: string }) =>
  trackEvent('referral_screen_viewed', props);
export const trackReferralApplied = (props: {
  referral_code: string;
  signup_method?: string;
}) => trackEvent('referral_applied', props);
export const trackLoginFailed = (props: { login_method: string; failure_reason: string }) =>
  trackEvent('login_failed', props);
export const trackWalletConnected = (props: { wallet_type: string; chain_id: string }) =>
  trackEvent('wallet_connected', props);

// ==========================================
// Wallet & Finance
// ==========================================
export const trackDepositStarted = (props: {
  deposit_method: string;
  amount: number;
  currency: string;
}) => trackEvent('deposit_started', props);
export const trackDepositCompleted = (props: {
  deposit_method: string;
  amount: number;
  currency: string;
  tx_hash: string;
}) => trackEvent('deposit_completed', props);
export const trackDepositFailed = (props: {
  deposit_method: string;
  amount: number;
  error_code: string;
}) => trackEvent('deposit_failed', props);

export const trackWithdrawalStarted = (props: {
  amount: number;
  currency: string;
  destination_type: string;
}) => trackEvent('withdrawal_started', props);
export const trackWithdrawalCompleted = (props: {
  amount: number;
  currency: string;
  fee: number;
  tx_hash: string;
}) => trackEvent('withdrawal_completed', props);
export const trackWithdrawalFailed = (props: { amount: number; error_code: string }) =>
  trackEvent('withdrawal_failed', props);

export const trackPayoutClaimed = (props: {
  market_id: string;
  amount: number;
  pnl: number;
  tx_hash: string;
}) => trackEvent('payout_claimed', props);
export const trackApiErrorOccurred = (props: {
  endpoint: string;
  http_status_code: number;
  error_code: string;
  user_action: string;
}) => trackEvent('api_error_occurred', props);

// ==========================================
// Trading Funnel
// ==========================================
export const trackMarketListViewed = (props: {
  category_filter: string;
  sort_by: string;
  total_markets_shown: number;
  topic_tag: string;
}) => trackEvent('market_list_viewed', props);
export const trackCategoryTabClicked = (props: {
  tab_name: string;
  tab_type: string;
  previous_tab: string;
  markets_shown_after: number;
}) => trackEvent('category_tab_clicked', props);
export const trackMarketDetailViewed = (props: {
  market_id: string;
  market_title: string;
  category: string;
  total_volume: number;
  end_date: string;
}) => trackEvent('market_detail_viewed', props);
export const trackMarketSearched = (props: { query: string; results_count: number }) =>
  trackEvent('market_searched', props);

export const trackTradeFlowStarted = (props: {
  market_id: string;
  market_title: string;
  outcome_selected: string;
  current_price: number;
  trade_side: string;
  order_type: string;
  category: string;
}) => trackEvent('trade_flow_started', props);
export const trackTradeSubmitted = (props: {
  market_id: string;
  outcome_selected: string;
  amount: number;
  shares: number;
  price_per_share: number;
  trade_type: string;
  trade_side: string;
  order_type: string;
  amount_preset_used: boolean;
  category: string;
}) => trackEvent('trade_submitted', props);
export const trackTradeConfirmed = (props: {
  market_id: string;
  trade_id: string;
  outcome_selected: string;
  amount: number;
  tx_hash: string;
  latency_ms: number;
  trade_side: string;
  order_type: string;
  category: string;
}) => trackEvent('trade_confirmed', props);
export const trackTradeFailed = (props: {
  market_id: string;
  outcome_selected: string;
  amount: number;
  error_code: string;
  trade_side: string;
  order_type: string;
}) => trackEvent('trade_failed', props);
export const trackTradeCancelled = (props: {
  market_id: string;
  outcome_selected: string;
  amount_entered: number;
  cancel_step: string;
  trade_side: string;
  order_type: string;
}) => trackEvent('trade_cancelled', props);

export const trackPositionSold = (props: {
  market_id: string;
  outcome: string;
  shares_sold: number;
  sale_price: number;
  pnl: number;
  pnl_pct: number;
}) => trackEvent('position_sold', props);

export const trackLimitOrderPlaced = (props: {
  market_id: string;
  market_title: string;
  outcome_selected: string;
  limit_price: number;
  amount: number;
  trade_side: string;
}) => trackEvent('limit_order_placed', props);
export const trackLimitOrderFilled = (props: {
  market_id: string;
  trade_id: string;
  outcome_selected: string;
  limit_price: number;
  amount: number;
  trade_side: string;
  time_to_fill_sec: number;
}) => trackEvent('limit_order_filled', props);
export const trackLimitOrderCancelled = (props: {
  market_id: string;
  outcome_selected: string;
  limit_price: number;
  amount: number;
  time_open_sec: number;
}) => trackEvent('limit_order_cancelled', props);
export const trackLimitOrderExpired = (props: {
  market_id: string;
  outcome_selected: string;
  limit_price: number;
  amount: number;
  time_open_sec: number;
}) => trackEvent('limit_order_expired', props);

// ==========================================
// Engagement
// ==========================================
export const trackMarketBookmarked = (props: {
  market_id: string;
  market_title: string;
  category: string;
}) => trackEvent('market_bookmarked', props);
export const trackPortfolioViewed = (props: {
  total_positions: number;
  total_value: number;
  total_pnl: number;
}) => trackEvent('portfolio_viewed', props);
export const trackMarketResolvedViewed = (props: {
  market_id: string;
  winning_outcome: string;
  user_position_outcome: string;
  pnl: number;
}) => trackEvent('market_resolved_viewed', props);
